import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';

/**
 * Configuración para un heatmap
 */
export interface HeatmapConfig {
  id?: string;
  points: [number, number][];
  radius?: number;
  intensity?: number;
  opacity?: number;
  weight?: number;
  gradient?: string[]; // Array de colores para el gradiente
}

/**
 * HeatmapLayerManager
 *
 * Manager especializado para renderizar mapas de calor (heatmaps) en Mapbox.
 *
 * Características:
 * - Visualización de densidad de puntos
 * - Gradiente de colores configurable
 * - Radio e intensidad ajustables
 * - NO usa el patrón de EntityLayerManager (geometrías especiales)
 *
 * Uso típico:
 * - Mapas de calor de luminarias con cortes
 * - Densidad de eventos
 * - Concentración de alarmas
 */
@Injectable({
  providedIn: 'root',
})
export class HeatmapLayerManager {
  private map: mapboxgl.Map | null = null;
  private sourceId = 'heatmap-source';
  private layerId = 'heatmap-layer';
  private isInitialized = false;

  // Gradiente por defecto (azul → cian → verde → amarillo → rojo)
  private readonly DEFAULT_GRADIENT = [
    '#00f', // Azul (0%)
    '#0ff', // Cian (25%)
    '#0f0', // Verde (50%)
    '#ff0', // Amarillo (75%)
    '#f00', // Rojo (100%)
  ];

  // ========== LIFECYCLE ==========

  initialize(map: mapboxgl.Map): void {
    if (this.isInitialized) {
      console.warn('⚠️ HeatmapLayerManager ya está inicializado');
      return;
    }

    this.map = map;
    this.createSourceAndLayer();
    this.isInitialized = true;
  }

  cleanup(): void {
    if (!this.map) return;

    if (this.map.getLayer(this.layerId)) {
      this.map.removeLayer(this.layerId);
    }

    if (this.map.getSource(this.sourceId)) {
      this.map.removeSource(this.sourceId);
    }

    this.map = null;
    this.isInitialized = false;
  }

  // ========== CREACIÓN ==========

  private createSourceAndLayer(): void {
    if (!this.map) return;

    // Source para heatmap
    if (!this.map.getSource(this.sourceId)) {
      this.map.addSource(this.sourceId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
        buffer: 0, // OPTIMIZACIÓN: Sin buffer para mejor performance
        maxzoom: 16, // OPTIMIZACIÓN: Limitar zoom máximo
      });
    }

    // Layer de heatmap
    if (!this.map.getLayer(this.layerId)) {
      this.map.addLayer({
        id: this.layerId,
        type: 'heatmap',
        source: this.sourceId,
        paint: {
          // Radio del punto (en píxeles)
          'heatmap-radius': ['coalesce', ['get', 'radius'], 30],

          // Peso de cada punto
          'heatmap-weight': ['coalesce', ['get', 'weight'], 1],

          // Opacidad global del layer (se puede actualizar dinámicamente en updateHeatmap)
          'heatmap-opacity': 0.6,

          // Gradiente de colores (se actualizará dinámicamente)
          'heatmap-color': [
            'interpolate',
            ['linear'],
            ['heatmap-density'],
            0,
            'rgba(0, 0, 255, 0)', // Azul transparente
            0.2,
            'rgba(0, 255, 255, 1)', // Cian
            0.4,
            'rgba(0, 255, 0, 1)', // Verde
            0.6,
            'rgba(255, 255, 0, 1)', // Amarillo
            0.8,
            'rgba(255, 128, 0, 1)', // Naranja
            1,
            'rgba(255, 0, 0, 1)', // Rojo
          ],
        },
      });
    }
  }

  // ========== DATOS ==========

  /**
   * Actualiza el heatmap con nuevos puntos
   */
  updateHeatmap(config: HeatmapConfig): void {
    if (!this.map || !this.isInitialized) {
      console.warn('⚠️ HeatmapLayerManager no está inicializado');
      return;
    }

    const features = config.points.map((point) =>
      this.pointToFeature(point, config),
    );

    const geojson: GeoJSON.FeatureCollection<GeoJSON.Point> = {
      type: 'FeatureCollection',
      features,
    };

    const source = this.map.getSource(this.sourceId) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(geojson);
    }

    // Actualizar gradiente si se especificó uno custom
    if (config.gradient && config.gradient.length > 0) {
      this.updateGradient(config.gradient);
    }

    // Actualizar opacidad si se especificó
    if (config.opacity !== undefined && this.map.getLayer(this.layerId)) {
      this.map.setPaintProperty(
        this.layerId,
        'heatmap-opacity',
        config.opacity,
      );
    }
  }

  /**
   * Limpia el heatmap
   */
  clearHeatmap(): void {
    if (!this.map || !this.isInitialized) return;

    const emptyGeoJSON: GeoJSON.FeatureCollection<GeoJSON.Point> = {
      type: 'FeatureCollection',
      features: [],
    };

    const source = this.map.getSource(this.sourceId) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(emptyGeoJSON);
    }
  }

  // ========== CONVERSIÓN ==========

  private pointToFeature(
    point: [number, number],
    config: HeatmapConfig,
  ): GeoJSON.Feature<GeoJSON.Point> {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: point,
      },
      properties: {
        radius: config.radius || 30,
        weight: config.weight || 1,
        intensity: config.intensity || 1,
        opacity: config.opacity !== undefined ? config.opacity : 0.8,
      },
    };
  }

  // ========== GRADIENTE ==========

  /**
   * Actualiza el gradiente de colores del heatmap
   */
  private updateGradient(colors: string[]): void {
    if (!this.map || !this.map.getLayer(this.layerId)) return;

    // IMPORTANTE: density=0 siempre transparente para no pintar zonas sin datos.
    // Los colores del gradiente se distribuyen entre 0.1 y 1.
    const steps = colors.length;
    const interpolation: mapboxgl.Expression = [
      'interpolate',
      ['linear'],
      ['heatmap-density'],
      0,
      'rgba(0, 0, 0, 0)', // Transparente donde no hay datos
      ...colors.flatMap((color, index) => {
        const density = 0.1 + (index / (steps - 1)) * 0.9;
        return [density, color];
      }),
    ];

    this.map.setPaintProperty(this.layerId, 'heatmap-color', interpolation);
  }

  /**
   * Establece el gradiente por defecto
   */
  resetGradient(): void {
    this.updateGradient(this.DEFAULT_GRADIENT);
  }

  // ========== CONFIGURACIÓN ==========

  /**
   * Actualiza el radio del heatmap
   */
  setRadius(radius: number): void {
    if (!this.map || !this.map.getLayer(this.layerId)) return;
    this.map.setPaintProperty(this.layerId, 'heatmap-radius', radius);
  }

  /**
   * Actualiza la intensidad del heatmap
   */
  setIntensity(intensity: number): void {
    if (!this.map || !this.map.getLayer(this.layerId)) return;
    this.map.setPaintProperty(this.layerId, 'heatmap-intensity', intensity);
  }

  /**
   * Actualiza la opacidad del heatmap
   */
  setOpacity(opacity: number): void {
    if (!this.map || !this.map.getLayer(this.layerId)) return;
    this.map.setPaintProperty(this.layerId, 'heatmap-opacity', opacity);
  }

  // ========== VISIBILIDAD ==========

  setVisibility(visible: boolean): void {
    if (!this.map) return;

    if (this.map.getLayer(this.layerId)) {
      this.map.setLayoutProperty(
        this.layerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }
  }

  getVisibility(): boolean {
    if (!this.map || !this.map.getLayer(this.layerId)) return false;
    return this.map.getLayoutProperty(this.layerId, 'visibility') === 'visible';
  }
}
